import { MONTHS } from '@/constants'

/**
 * Format a Date (or ISO string the Date constructor can parse) as
 * `YYYY-MM-DD` using local-time fields. Useful for sending date-only values
 * to APIs that compare against `sold_at`-style columns. Returns `null` for
 * a falsy input so callers can spread the result.
 */
export const formatIsoDate = (date) => {
  if (!date) return null
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  const y  = d.getFullYear()
  const m  = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/** Local midnight (start of the current day) as a Date. */
export const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * `YYYY-MM-DD` for `days` days before `from` (default: today). Local time;
 * mirrors what users see on the device clock rather than UTC.
 */
export const isoDateDaysAgo = (days, from = new Date()) => {
  const d = new Date(from)
  d.setDate(d.getDate() - days)
  return formatIsoDate(d)
}

/**
 * Human date-range label in **local** time. A single day (or equal start/end)
 * reads `"Mon D, YYYY"`; a span reads `"Mon D – Mon D, YYYY"` with the start
 * year omitted. Accepts `Date` objects or parseable strings — a bare
 * `YYYY-MM-DD` is treated as local midnight to avoid the UTC day-shift.
 * Returns `''` when `start` is missing/invalid.
 */
export const formatDateRangeLabel = (start, end) => {
  const toDate = (value) => {
    if (!value) return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
    const str = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value
    const d = new Date(str)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const startDate = toDate(start)
  if (!startDate) return ''
  const endDate = toDate(end)

  const monthDay     = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const monthDayYear = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const sameDay = endDate && startDate.getTime() === endDate.getTime()
  if (sameDay) return monthDayYear(startDate)
  return `${monthDay(startDate)} – ${monthDayYear(endDate ?? startDate)}`
}

export const countOfDaysAgo = (data) => {
  const comparisonDate = new Date(data)
  const currentDate = new Date()

  const timeDifference = currentDate - comparisonDate

  const daysAgo = Math.floor(timeDifference / (1000 * 60 * 60 * 24))

  return daysAgo
}

export const formatDate = (dateString) => {
  const date = new Date(dateString) // Convert to Date object
  const month = MONTHS[date.getUTCMonth()] // Get month abbreviation
  const day = date.getUTCDate() // Get day
  const year = date.getUTCFullYear() // Get year

  return `${month} ${day}, ${year}` // Format the date
}

export const isExpired = (dateString) => {
  const now = new Date() 
  const dateToCheck = new Date(dateString) 

  return dateToCheck < now
}

export const formatTimeLabel = (hour, minute) => {
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const normalizedHour = hour % 12 || 12
  const minuteStr = minute.toString().padStart(2, '0')

  return `${normalizedHour}:${minuteStr} ${suffix}`
}

export const formatHourToMeridiemLabel = (hour) => {
  const h = Number(hour)

  if (Number.isNaN(h)) return ''

  const suffix = h >= 12 ? 'PM' : 'AM'
  const normalizedHour = h % 12 || 12

  return `${normalizedHour}:00 ${suffix.toLowerCase()}`
}

export const formatDateWithTime = (dateString) => {
  const date = new Date(dateString)
  
  // Format date part
  const month = MONTHS[date.getUTCMonth()]
  const day = date.getUTCDate()
  const year = date.getUTCFullYear()

  // Format time part
  let hours = date.getUTCHours()
  const minutes = date.getUTCMinutes().toString().padStart(2, '0')
  const seconds = date.getUTCSeconds().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  
  // Convert hours to 12-hour format
  hours = hours % 12
  hours = hours ? hours : 12 // Handle midnight (0)
  
  return `${month} ${day}, ${year} • ${hours}:${minutes}:${seconds} ${ampm} EDT`
}

export const timeAgo = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now - date) / 1000)

  if (diffInSeconds < 60) {
    return diffInSeconds <= 1 ? 'just now' : `${diffInSeconds} seconds ago`
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? 'a minute ago' : `${diffInMinutes} minutes ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours === 1) {
    return 'an hour ago'
  }
  if (diffInHours > 1 && diffInHours < 24) {
    return 'recently'
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return diffInDays === 1 ? 'a day ago' : `${diffInDays} days ago`
  }

  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return diffInWeeks === 1 ? 'a week ago' : `${diffInWeeks} weeks ago`
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return diffInMonths === 1 ? 'a month ago' : `${diffInMonths} months ago`
  }

  const diffInYears = Math.floor(diffInDays / 365)
  return diffInYears === 1 ? 'a year ago' : `${diffInYears} years ago`
}
